import { NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';

export type ExtractedRuleFilter = 'all' | 'do' | 'dont' | 'objective' | 'subjective';

export interface ExtractedRuleChip {
  id: ExtractedRuleFilter;
  label: string;
  count: number;
}

export interface NumberedExtractedRule {
  rule: NoodleQuestionDto;
  number: number;
}

export function questionsFromGroup(value: unknown): NoodleQuestionDto[] {
  if (!value || typeof value !== 'object') {
    return [];
  }
  const questions = (value as { questions?: unknown }).questions;
  if (!Array.isArray(questions)) {
    return [];
  }
  return questions.filter(isExtractedRule);
}

export function extractedRuleCounts(rules: readonly NoodleQuestionDto[]) {
  const dos = rules.filter((rule) => rule.type === 'do').length;
  const subjective = rules.filter((rule) => rule.subjectiveType === 1).length;
  return {
    all: rules.length,
    dos,
    donts: rules.length - dos,
    subjective,
    objective: rules.length - subjective,
  };
}

export function extractedRuleChips(rules: readonly NoodleQuestionDto[]): ExtractedRuleChip[] {
  const counts = extractedRuleCounts(rules);
  return [
    { id: 'all', label: 'All rules', count: counts.all },
    { id: 'do', label: "Do's", count: counts.dos },
    { id: 'dont', label: "Don'ts", count: counts.donts },
  ];
}

/** Numbers come from the full list so "#03" stays put when a filter hides earlier rules. */
export function visibleExtractedRules(
  rules: readonly NoodleQuestionDto[],
  filter: ExtractedRuleFilter,
): NumberedExtractedRule[] {
  return rules
    .map((rule, index) => ({ rule, number: index + 1 }))
    .filter(({ rule }) => matchesExtractedRule(rule, filter));
}

export function extractedRuleSummary(rules: readonly NoodleQuestionDto[]): string {
  const counts = extractedRuleCounts(rules);
  return `We've extracted ${counts.all} checkable rules from the brief — ${counts.dos} things creators must do and ${counts.donts} things they must not. Every piece of content is scored against all of them.`;
}

export function extractedRuleTag(rule: NoodleQuestionDto): string {
  return rule.generalised ? 'Library rule' : 'Brief-specific';
}

export function extractedRuleNumberLabel(index: number): string {
  return `#${String(index).padStart(2, '0')}`;
}

export function extractedRuleHasContext(rule: NoodleQuestionDto): boolean {
  return (rule.enrichments ?? []).some((item) => item.text.trim().length > 0);
}

export function extractedRuleFilterClass(selected: boolean): string {
  return selected
    ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
    : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
}

export function extractedRuleCountClass(selected: boolean): string {
  return selected
    ? 'bg-gray-300 text-gray-900 dark:bg-white/20 dark:text-gray-200'
    : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400';
}

function matchesExtractedRule(rule: NoodleQuestionDto, filter: ExtractedRuleFilter): boolean {
  switch (filter) {
    case 'do':
    case 'dont':
      return rule.type === filter;
    case 'objective':
      return rule.subjectiveType === 0;
    case 'subjective':
      return rule.subjectiveType === 1;
    default:
      return true;
  }
}

function isExtractedRule(value: unknown): value is NoodleQuestionDto {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const rule = value as NoodleQuestionDto;
  return typeof rule.id === 'string' && (rule.type === 'do' || rule.type === 'dont') && typeof rule.statement === 'string';
}
